import { graphql } from 'gatsby';
import useStaticFooter from 'hooks/useStaticFooter';
import useStaticHeader from 'hooks/useStaticHeader';
import useStaticSite from 'hooks/useStaticSite';
import SEO from 'layouts/Seo';
import { useLocation } from 'react-use';

import ConversionPanel from 'components/ConversionPanel';
import Footer from 'components/Footer';
import Header from 'components/Header';
import Hero from 'components/Hero';
import PostListing from 'components/Resources/PostListing';

import 'styles/templates/PostListing.css';

import type { ConversionPanelProps } from 'components/ConversionPanel';
import type { FooterProps } from 'components/Footer';
import type { HeaderProps } from 'components/Header';
import type { HeroProps } from 'components/Hero';
import type { PageProps } from 'gatsby';
import type {
  ContentfulLayoutSection,
  ContentfulTaxonomyTag,
  ContentfulTaxonomyTagConnection,
  ContentfulTemplatePage,
  ContentfulTemplateResourceCenterConnection,
} from 'graphqlTypes';
import type { SectionProps } from 'molecules/Section';
import type { FC } from 'react';
import type { PostUnion } from 'types';

interface PostDataProps {
  blogHeroSection: ContentfulLayoutSection;
  caseStudyHeroSection: ContentfulLayoutSection;
  conversionPanelSection: ContentfulLayoutSection;
  conversionPanelSection2: ContentfulLayoutSection;
  customerStoryHeroSection: ContentfulLayoutSection;
  newsHeroSection: ContentfulLayoutSection;
  pageData: ContentfulTemplatePage;
  recentResourceCenterPosts: ContentfulTemplateResourceCenterConnection;
  resourceGuideHeroSection: ContentfulLayoutSection;
  resourceTemplateHeroSection: ContentfulLayoutSection;
  resourceToolHeroSection: ContentfulLayoutSection;
  tags: ContentfulTaxonomyTagConnection;
}

type pageContextProps = {
  activeTag?: ContentfulTaxonomyTag;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  pageNumber: number;
  posts: PostUnion[];
  resourceType: string;
  slug: string;
  totalPages: number;
  totalPosts: number;
  tags: ContentfulTaxonomyTag[];
  tagId?: string;
  tagName?: string;
};

export const scrollTo = (id: string, space: number) => {
  const initialPosition = window.scrollY;
  const element = id ? document.getElementById(id) : null;

  if (!element) {
    return false;
  }

  const destinationPos = initialPosition + element.getBoundingClientRect().top - space;
  window.scrollTo({
    top: destinationPos,
    behavior: 'smooth',
  });
};

const Post: FC<PageProps<PostDataProps>> = ({
  data: {
    blogHeroSection,
    caseStudyHeroSection,
    conversionPanelSection,
    conversionPanelSection2,
    customerStoryHeroSection,
    newsHeroSection,
    resourceGuideHeroSection,
    resourceTemplateHeroSection,
    resourceToolHeroSection,
  },
  pageContext,
}) => {
  const { header } = useStaticHeader();
  const { footer } = useStaticFooter();

  const { activeTag, hasNextPage, hasPreviousPage, pageNumber, posts, resourceType, tags, totalPages } =
    pageContext as pageContextProps;

  const generateHeroProps = (type: string) => {
    switch (type) {
      case 'blog':
        return blogHeroSection;
      case 'news':
        return newsHeroSection;
      case 'case-studies':
        return caseStudyHeroSection;
      case 'templates':
        return resourceTemplateHeroSection;
      case 'tools':
        return resourceToolHeroSection;
      case 'guides':
        return resourceGuideHeroSection;
      case 'customer-stories':
        return customerStoryHeroSection;
      default:
        return null;
    }
  };
  const heroProps = generateHeroProps(resourceType);

  return (
    <div>
      {header && <Header {...(header as HeaderProps)} />}
      <main>
        {heroProps && <Hero {...(heroProps['component'] as HeroProps)} {...(heroProps as SectionProps)} />}
        <PostListing
          activeTag={activeTag}
          currentPageNumber={pageNumber || 0}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          posts={posts}
          resourceType={resourceType}
          tags={tags}
          totalPages={totalPages}
        />
        {conversionPanelSection && (
          <ConversionPanel
            {...(conversionPanelSection['component'] as ConversionPanelProps)}
            {...(conversionPanelSection as SectionProps)}
          />
        )}
        {resourceType === 'case-studies' && conversionPanelSection2 && (
          <ConversionPanel
            {...(conversionPanelSection2['component'] as ConversionPanelProps)}
            {...(conversionPanelSection2 as SectionProps)}
          />
        )}
      </main>
      {footer && <Footer {...(footer as FooterProps)} background="blue900" padding="small" contained />}
    </div>
  );
};

export const Head: FC<PageProps<PostDataProps>> = ({ data: { pageData }, pageContext }) => {
  const seo = pageData?.seo;
  const { site } = useStaticSite();
  const { href } = useLocation();
  const indexable = !href?.includes('/page/') && seo?.indexable;

  const { pageNumber, resourceType, tagName, slug } = pageContext as pageContextProps;
  const capitalizeResourcedType = resourceType
    ?.split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const canonicalUrl = `${site.siteMetadata.siteUrl}/${slug}${pageNumber > 1 ? `/page/${pageNumber}` : ''}`;

  return (
    <SEO
      {...seo}
      pageTitle={`${capitalizeResourcedType}${tagName ? ' - ' + tagName : ''}${
        pageNumber > 1 ? ' - Page ' + pageNumber : ''
      }`}
      indexable={pageNumber < 5 || indexable}
      slug={slug}
      canonicalUrl={canonicalUrl}
    />
  );
};

export default Post;

export const postQuery = graphql`
  query templatePostListingQuery($slug: String) {
    pageData: contentfulTemplatePage(slug: { eq: $slug }) {
      ...contentfulTemplatePage
    }
    tags: allContentfulTaxonomyTag {
      nodes {
        ...contentfulTaxonomyTag
      }
    }
    blogHeroSection: contentfulLayoutSection(contentful_id: { eq: "6IOk1CiHOFUpNP8Vy4nbYv" }) {
      ...contentfulLayoutSection
    }
    newsHeroSection: contentfulLayoutSection(contentful_id: { eq: "2UXmA7fSeOjk5ecFXix3HO" }) {
      ...contentfulLayoutSection
    }
    caseStudyHeroSection: contentfulLayoutSection(contentful_id: { eq: "6kAh074KnuitShWqNRLCx" }) {
      ...contentfulLayoutSection
    }
    customerStoryHeroSection: contentfulLayoutSection(contentful_id: { eq: "36TFNX6IG6mw25UvGeQL2i" }) {
      ...contentfulLayoutSection
    }
    resourceTemplateHeroSection: contentfulLayoutSection(contentful_id: { eq: "4LuFbhwp2itHFTy42r9QeC" }) {
      ...contentfulLayoutSection
    }
    resourceGuideHeroSection: contentfulLayoutSection(contentful_id: { eq: "5aKRxvH0t1tXJGrkW21AYV" }) {
      ...contentfulLayoutSection
    }
    resourceToolHeroSection: contentfulLayoutSection(contentful_id: { eq: "6LLiDpBwPa308rB4H2Vrop" }) {
      ...contentfulLayoutSection
    }
    conversionPanelSection: contentfulLayoutSection(contentful_id: { eq: "4KXA4lFTbrhPADmbxqr6Si" }) {
      ...contentfulLayoutSection
    }
    conversionPanelSection2: contentfulLayoutSection(contentful_id: { eq: "3IZIGTGBxARy1xd11DCCgx" }) {
      ...contentfulLayoutSection
    }
  }
`;
